import {
  Autocomplete,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  TextField,
  Typography,
  Breadcrumbs,
  Link,
} from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import OmsCommentsLogs from './OmsCommentsLogs';
import { omsApi } from 'src/api/omsApis';
import { useParams } from 'react-router';
import axiosInstance from 'src/utils/axios';
import { debounce } from 'lodash';
import { AddOutlined } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { Link as RouterLink } from 'react-router-dom';

function OrderDetails() {
  const [order, setOrder] = useState({
    _id: '',
    description: '',
    status: '',
    isExternal: null,
    products: [],
    organization: {
      _id: '',
      name: '',
      email: '',
    },
    createdBy: {
      _id: '',
      name: '',
      email: '',
    },
    createdAt: null,
  });
  const [organizations, setOrganizations] = useState([]);
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);

  const getAllOrganizations = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_OMS_URL}/autoComplete/organizations`
      );
      if (response.status === 200) {
        setOrganizations(response.data);
      }
    } catch (error) {
      console.error('Error fetching organizations', error);
    }
  };
  const { id } = useParams();

  const fetchOrderDetails = async () => {
    try {
      const response = await omsApi.getOmsOrderDetailsById(id);

      setOrder(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const updateOrderDetails = async (updatedOrder) => {
    try {
      await omsApi.updateOrder(updatedOrder);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const debouncedUpdateOrderDetails = debounce((payload) => {
    updateOrderDetails(payload);
  }, 1000);

  useEffect(() => {
    fetchOrderDetails();
    getAllOrganizations();
  }, [id]);
  // Calculate totals
  const subtotal = order?.products?.reduce(
    (sum, product) => sum + product.actualPrice * product.quantity,
    0
  );

  // Calculate total discount based on each product's discount
  const totalDiscount = order?.products?.reduce(
    (sum, product) =>
      sum + (product.actualPrice * product.quantity * product.discount) / 100,
    0
  );

  // const tax = subtotal * 0.1;
  const totalDue = subtotal - totalDiscount;

  const handleCreateInvoice = async () => {
    setIsCreatingInvoice(true);
    const payload = {
      orderId: order._id,
      description: order.description,
      billedTo: {
        name: order.organization.name,
        email: order.organization.email,
      },
      billedBy: {
        orgId: order.createdBy._id,
        name: order?.createdBy?.name,
        email: order?.createdBy?.email,
      },
      tax: 0,
      discount: totalDiscount,
      subTotalPrice: subtotal,
      totalPrice: totalDue,
    };
    try {
      const response = await omsApi.createInvoice(payload);
      if (response.status === 201) {
        toast.success('Invoice created successfully');
      }
    } catch (error) {
      console.error('Error :', error);
    } finally {
      setIsCreatingInvoice(false);
    }
  };
  const handleQuantityChange = (productId, delta) => {
    setOrder((prevOrder) => {
      const updatedProducts = prevOrder.products.map((product) => {
        if (product.productId === productId) {
          return {
            ...product,
            quantity: Math.max(0, product.quantity + delta), // Ensure quantity doesn't go below 0
          };
        }
        return product;
      });

      const updatedOrder = {
        description: prevOrder.description,
        products: updatedProducts.map((product) => ({
          productId: product.productId,
          productName: product.productName,
          duration: product.duration,
          sku: product.sku,
          actualPrice: product.actualPrice,
          discount: product.discount,
          buyInPrice: product.buyInPrice,
          quantity: product.quantity,
        })),
        organization: {
          orgId: prevOrder.organization._id,
          name: prevOrder.organization.name,
          email: prevOrder.organization.email,
        },
      };

      const payload = {
        data: updatedOrder,
        _id: prevOrder._id,
      };

      debouncedUpdateOrderDetails(payload); // Use debounced function for API call
      return { ...prevOrder, products: updatedProducts };
    });
  };

  const handleUpdate = (newValue, orderID) => {
    console.log('newValue', newValue);
    if (newValue) {
      setOrder((prevOrder) => {
        const updatedOrder = {
          description: newValue.description,
          products: newValue.products.map((product) => ({
            productId: product.productId,
            productName: product.productName,
            duration: product.duration,
            sku: product.sku,
            actualPrice: product.actualPrice,
            discount: product.discount,
            buyInPrice: product.buyInPrice,
            quantity: product.quantity,
          })),
          organization: {
            orgId: newValue.organization._id,
            name: newValue.organization.name,
            email: newValue.organization.email,
          },
        };
        const payload = {
          data: updatedOrder,
          _id: orderID,
        };
        updateOrderDetails(payload); // Call updateOrderDetails with the updated payload
        return { ...prevOrder, organization: newValue.organization };
      });
    }
    setIsEditingCustomer(false); // Exit edit mode after selection or cancellation
  };

  const handleOrganizationChange = (newValue) => {
    if (newValue) {
      const updatedOrder = {
        ...order,
        organization: newValue,
      };
      setOrder(updatedOrder);
      handleUpdate(updatedOrder, order._id);
    }
    setIsEditingCustomer(false); // Exit edit mode after selection or cancellation
  };

  return (
    <Box sx={{ padding: '20px' }}>
      <Breadcrumbs aria-label="breadcrumb" style={{ marginBottom: '20px' }}>
        <Link
          color="textPrimary"
          component={RouterLink}
          to="/oms/orders"
          variant="subtitle2">
          Orders
        </Link>
        <Typography color="textPrimary">Order Details</Typography>
      </Breadcrumbs>

      <Box
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          marginBottom: '10px',
        }}>
        <Typography style={{ fontSize: '22px', fontWeight: '600' }}>
          Order Name
        </Typography>
        <Button
          type="button"
          variant="contained"
          color="secondary"
          onClick={handleCreateInvoice}
          disabled={isCreatingInvoice}>
          <AddOutlined />
          {isCreatingInvoice ? 'Creating...' : 'Create Invoice'}
        </Button>
      </Box>

      <Card sx={{ borderRadius: '8px', padding: '20px' }}>
        <Box sx={{ paddingLeft: '10px', marginBottom: '10px' }}>
          <Typography
            style={{
              fontSize: '18px',
              fontWeight: '700',
              marginBottom: '10px',
            }}>
            Details
          </Typography>
          <Divider />
        </Box>
        <Grid container>
          <Grid item xs={4} style={{ position: 'relative' }}>
            <Box
              style={{ fontFamily: 'Arial, sans-serif', padding: '10px 20px' }}>
              <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
                {/* Left Details */}
                <Box style={{ flex: 1 }}>
                  <Grid container style={{ marginBottom: '10px' }}>
                    <Grid item xs={6}>
                      <Typography color="#A0A9B2">Customer:</Typography>
                      {isEditingCustomer ? (
                        <Autocomplete
                          options={organizations}
                          getOptionLabel={(option) => option.name}
                          value={order.organization}
                          onChange={(event, newValue) =>
                            handleOrganizationChange(newValue)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder="Select organization"
                            />
                          )}
                        />
                      ) : (
                        <Typography
                          fontWeight={700}
                          onClick={() => setIsEditingCustomer(true)}
                          style={{ cursor: 'pointer' }}>
                          {order?.organization?.name}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography color="#A0A9B2">
                        Customer Contact info:
                      </Typography>
                      <Typography fontWeight={700}>
                        {order?.organization?.email}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Typography color="#A0A9B2">Description:</Typography>
                  <Typography
                    style={{
                      maxHeight: '100px',
                      overflow: 'auto',
                      maxWidth: '406px',
                      overflowWrap: 'break-word',
                      paddingRight: '3px',
                    }}>
                    {order?.description}
                  </Typography>
                  <Grid
                    container
                    style={{ position: 'absolute', bottom: 10, left: 0 }}>
                    <Grid item xs={6} style={{ padding: '10px' }}>
                      <Typography
                        color="#A0A9B2"
                        sx={{ fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                        Created by: {order?.createdBy?.name}
                      </Typography>
                    </Grid>

                    <Grid item xs={6} style={{ padding: '10px' }}>
                      <Typography
                        color="#A0A9B2"
                        sx={{ fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                        Created at:
                        {order?.createdAt &&
                          new Date(order.createdAt)
                            .toLocaleString('en-US', {
                              month: 'short',
                              day: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false,
                            })
                            .replace(',', ' -')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Right Table */}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={8}>
            <div style={{ flex: 2 }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  marginBottom: '20px',
                  border: '1px solid #EDE2F7',
                }}>
                <thead
                  style={{
                    background: '#FAFAFB',
                    border: '1px solid #EDE2F7',
                  }}>
                  <tr style={{ border: '1px solid #EDE2F7' }}>
                    <th
                      style={{
                        borderBottom: '1px solid #ddd',
                        padding: '8px',
                        color: '#8492A6',
                        textAlign: 'center',
                      }}>
                      Product Name
                    </th>
                    <th
                      style={{
                        borderBottom: '1px solid #ddd',
                        padding: '8px',
                        color: '#8492A6',
                        textAlign: 'center',
                      }}>
                      Unit Price (EUR)
                    </th>
                    <th
                      style={{
                        borderBottom: '1px solid #ddd',
                        padding: '8px',
                        color: '#8492A6',
                        textAlign: 'center',
                      }}>
                      Quantity
                    </th>
                    <th
                      style={{
                        borderBottom: '1px solid #ddd',
                        padding: '8px',
                        color: '#8492A6',
                        textAlign: 'center',
                      }}>
                      Discount %
                    </th>
                    <th
                      style={{
                        borderBottom: '1px solid #ddd',
                        padding: '8px',
                        color: '#8492A6',
                        textAlign: 'center',
                      }}>
                      Price
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {order?.products?.map((product, index) => (
                    <tr
                      key={product.productId}
                      style={{ border: '1px solid #EDE2F7' }}>
                      <td
                        style={{
                          padding: '8px',
                          border: '1px solid #EDE2F7',
                          textAlign: 'center',
                        }}>
                        {product.productName} ({product.sku}) (
                        {product.duration})
                      </td>
                      <td
                        style={{
                          padding: '8px',
                          border: '1px solid #EDE2F7',
                          textAlign: 'center',
                        }}>
                        {product.actualPrice.toFixed(2)} EUR
                      </td>
                      <td
                        style={{
                          padding: '8px',
                          border: '1px solid #EDE2F7',
                          textAlign: 'center',
                        }}>
                        <Button
                          onClick={() =>
                            handleQuantityChange(product.productId, -1)
                          }>
                          -
                        </Button>
                        {product.quantity}
                        <Button
                          onClick={() =>
                            handleQuantityChange(product.productId, 1)
                          }>
                          +
                        </Button>
                      </td>
                      <td
                        style={{
                          padding: '8px',
                          border: '1px solid #EDE2F7',
                          textAlign: 'center',
                        }}>
                        {product.discount}%
                      </td>
                      <td
                        style={{
                          padding: '8px',
                          border: '1px solid #EDE2F7',
                          textAlign: 'center',
                        }}>
                        {(product.actualPrice * product.quantity).toFixed(2)}{' '}
                        EUR
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Box
                style={{
                  textAlign: 'right',
                  width: '30%',
                  placeSelf: 'flex-end',
                }}>
                <Grid container>
                  <Grid item xs={6} sx={{ textAlign: 'left' }}>
                    <Typography my="10px">Sub Total:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography fontWeight={700} my="10px">
                      €{subtotal.toFixed(2)}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={6}
                    sx={{ textAlign: 'left', borderBottom: '1px solid grey' }}>
                    <Typography my="10px">Total Discount:</Typography>
                  </Grid>
                  <Grid
                    item
                    xs={6}
                    sx={{ textAlign: 'right', borderBottom: '1px solid grey' }}>
                    <Typography my="10px" fontWeight={700}>
                      €{totalDiscount.toFixed(2)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sx={{ textAlign: 'left' }}>
                    <Typography my="10px">Total Due:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography my="10px" fontWeight={700}>
                      €{totalDue.toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </div>
          </Grid>
        </Grid>
      </Card>
      <Card style={{ marginTop: '10px', borderRadius: '8px' }}>
        <OmsCommentsLogs
          details={order}
          commentApiToCall={omsApi?.createOrderComment}
          logApiToCall={omsApi.getOrderLogs}
        />
      </Card>
      {/*  <Typography
        style={{ fontSize: '22px', fontWeight: '600', marginTop: '10px' }}>
        Invoice
      </Typography>
      <Card style={{ marginTop: '10px', borderRadius: '8px' }}>
        <InvoiceTable />
      </Card> */}
      {/* removed the above table as told by ahmed mujtaba */}
    </Box>
  );
}

export default OrderDetails;
